import {
  Stack,
  OutlinedInput,
  IconButton,
  Typography,
  Alert,
  LinearProgress,
  Box,
} from "@mui/material";
import { useState, useCallback, ChangeEvent } from "react";
import { useParams } from "react-router-dom";
import {
  useGenerateSmsSuggestionQuery,
  useSendSmsMutation,
} from "src/api/services/smsApi";
import { MatchParam } from "src/constants/matches";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import { isError } from "src/utils/isError";
import SendIcon from "@mui/icons-material/Send";
import MessageSuggestion from "src/shared/MessageSuggestion";
import { skipToken } from "@reduxjs/toolkit/query";
import AlertWithCopy from "src/components/AlertWithCopy";

interface Props {
  onSuccess?: () => void;
  phoneNumber: string | null;
  patientProfileId: string;
}

const NewSmsMessageForm = ({
  onSuccess,
  phoneNumber,
  patientProfileId,
}: Props) => {
  const { queue } = useParams<MatchParam<"QUEUE_TYPE">>();
  const errorLog = useSentryLogger("error");
  const [error, setError] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [sendSms] = useSendSmsMutation();
  const [requestSuggestion, setRequestSuggestion] = useState<boolean>(false);
  const [profileId, setProfileId] = useState<string | null>(null);
  const { data, refetch, isLoading, isFetching } =
    useGenerateSmsSuggestionQuery(profileId ?? skipToken);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setError("");
    setNewMessage(event.target.value);
  }, []);

  const handleGenerateSuggestion = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      setRequestSuggestion(true);
      setProfileId(patientProfileId);
    },
    [patientProfileId],
  );

  const handleCloseSuggestion = useCallback(() => {
    setProfileId(null);
    setRequestSuggestion(false);
  }, []);

  const sendMessage = useCallback(() => {
    if (phoneNumber == null) return;

    sendSms({
      body: newMessage,
      to: phoneNumber,
    })
      .unwrap()
      .catch((err) => {
        if (isError(err)) {
          setError(err.data.message);
        }
        errorLog(err);
      })
      .then(() => {
        setNewMessage("");
        onSuccess?.();
      });
  }, [errorLog, newMessage, onSuccess, phoneNumber, sendSms]);

  return (
    <Stack>
      <MessageSuggestion
        generate={handleGenerateSuggestion}
        onClose={handleCloseSuggestion}
        refetch={refetch}
        generated={!!data}
        isLoading={isLoading}
        requestSuggestion={requestSuggestion}
      />
      {requestSuggestion && data != null ? (
        <Box mt={1} mb={1}>
          {isFetching ? (
            <Stack>
              <LinearProgress />
              <Typography>Generating new suggestion...</Typography>
            </Stack>
          ) : (
            <AlertWithCopy text={data} copyText={data} />
          )}
        </Box>
      ) : null}
      <Stack gap={0}>
        <OutlinedInput
          error={error.length > 0}
          value={newMessage}
          onChange={handleChange}
          id="outlined-adornment"
          type="text"
          multiline
          maxRows={10}
          placeholder="Type here..."
          sx={{
            background: "#F8F8F8",
            borderRadius: "10px",
            width: "100%",
            color: "#000",
            gap: "1rem",
            padding: "10px 15px",
            mt: "auto",
          }}
          endAdornment={
            <IconButton
              sx={{ color: "#00B6B9" }}
              onClick={sendMessage}
              disabled={newMessage.length === 0}
            >
              <SendIcon />
            </IconButton>
          }
        />
        {error ? (
          <Typography color="error" variant="caption" ml={1}>
            {error}
          </Typography>
        ) : null}
        {queue == null ? (
          <Alert severity="warning">
            When SMS text outside of a task queue, tasks will not be completed
          </Alert>
        ) : null}
      </Stack>
    </Stack>
  );
};

export default NewSmsMessageForm;
